import { useState, useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import Navbar from "@/components/Common/Navbar";
import AdminBreadcrumb from "@/features/admin/components/AdminBreadcrumb";
import EditHashKeyForm from "@/features/manager/components/AddNewUser/EditHashKeyForm";
import { useGetHashKeys } from "@/features/auth/api/hash.queries";
import type { HashKeyApiResponse } from "@/features/auth/types/hashKeys.types";
import { Spinner } from "@/components/ui/spinner";

export default function EditUserContainer() {
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const search = useSearch({
    from: "/manager/subscription-management/edit-user/$hashKeyId",
  });

  // Fetch all hash keys
  const { data: hashKeysData, isLoading } = useGetHashKeys();

  // Find the specific hash key by ID from the route param
  const hashKeyData = useMemo(() => {
    if (!hashKeysData?.hash_keys || !search.hashKeyId) return null;
    return hashKeysData.hash_keys.find(
      (key: HashKeyApiResponse) => key.hash_key_id === search.hashKeyId,
    );
  }, [hashKeysData, search.hashKeyId]);

  const handleSuccess = () => {
    setIsSuccess(true);
  };

  if (isLoading) {
    return (
      <main className="scrollbar-thin flex h-screen min-h-0 flex-1 flex-col gap-7 overflow-y-auto p-3 px-4 pr-0 pl-6 transition-all duration-300">
        <div className="mb-5 flex-shrink-0">
          <Navbar subtitle="manager" />
        </div>

        <div className="flex-shrink-0 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
          <AdminBreadcrumb title="Edit Hash Key" />
        </div>

        <div className="min-h-0 flex-1 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
          <div className="flex min-h-0 w-full flex-row justify-start gap-8 pb-20">
            <div className="m-auto w-full max-w-[600px] gap-3">
              <div className="flex items-center justify-center p-8">
                <Spinner size="md" className="stroke-primary!" />
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!hashKeyData) {
    return (
      <main className="scrollbar-thin flex h-screen min-h-0 flex-1 flex-col gap-7 overflow-y-auto p-3 px-4 pr-0 pl-6 transition-all duration-300">
        <div className="mb-5 flex-shrink-0">
          <Navbar subtitle="manager" />
        </div>

        <div className="flex-shrink-0 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
          <AdminBreadcrumb title="Edit Hash Key" />
        </div>

        <div className="min-h-0 flex-1 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
          <div className="flex min-h-0 w-full flex-row justify-start gap-8 pb-20">
            <div className="m-auto w-full max-w-[600px] gap-3">
              <div className="rounded-lg bg-red-50 p-6 text-center">
                <p className="font-medium text-red-800">
                  Hash key not found. Please return to team management.
                </p>
                <button
                  onClick={() =>
                    navigate({ to: "/manager/subscription-management" })
                  }
                  className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                >
                  Back to Team Management
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="scrollbar-thin flex h-screen min-h-0 flex-1 flex-col gap-7 overflow-y-auto p-3 px-4 pr-0 pl-6 transition-all duration-300">
      <div className="mb-5 flex-shrink-0">
        <Navbar subtitle="manager" />
      </div>

      <div className="flex-shrink-0 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <AdminBreadcrumb
          title={`Edit hash key: ${hashKeyData.hash_key_name}`}
        />
      </div>

      <div className="min-h-0 flex-1 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <div className="flex min-h-0 w-full flex-row justify-start gap-8 pb-20">
          <div className="m-auto w-full max-w-[600px] gap-3">
            {isSuccess && (
              <div className="mb-6 rounded-lg bg-green-50 p-4">
                <p className="font-medium text-green-800">
                  Hash key has been successfully updated! Redirecting to team
                  management...
                </p>
              </div>
            )}

            <EditHashKeyForm
              hashKeyData={hashKeyData}
              onSuccess={handleSuccess}
            />
          </div>
        </div>
      </div>
    </main>
  );
}
